import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  PortalProps
} from "@chakra-ui/react"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { getAdminUrl } from "@pathwright/utils/src/url/admin"
import getPWAdminSSOUrl from "@pathwright/web-new/src/views/auth/utils/getPWAdminSSOUrl"
import { Fragment } from "react"
import { PathItemType, SchoolFragment } from "../api/generated"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { PWAdminCmdKMenu } from "./PWAdminCmdK"
import { usePWAdminCmdKContext } from "./PWAdminCmdKContext"
import PWAdminCmdKItems from "./PWAdminCmdKItems"
import "./PWAdminHint.css"
import { PWAdminCmdKOption, PWAdminHintModel } from "./types"
import useIsPWAdminUtilEnabled from "./useIsPWAdminUtilEnabled"
import useSupportInfo from "./useSupportInfo"

// The PWAdminHint will wrap a component providing a popover exposed on hover
// that will provide different hints or information to the user, such as admin
// links to the related model (i.e. if you were to wrap a Path with PWAdminHint,
// you'd see admin links to that Path's model.)

type PWAdminHintProps = {
  children: React.ReactNode
  model: PWAdminHintModel
  // Whether to wrap the children in a div in order to ensure PopoverTrigger works.
  // Note that the children will only ever be wrapped if the hint is enabled, so do not fear
  // any (potential) layout shifts when hovering over the component for non-superusers.
  wrap?: boolean
  // Optionally portal the popover to the end of the DOM.
  portal?: Partial<PortalProps> | boolean
}

export function getModelInfo(
  model: PWAdminHintModel,
  school: SchoolFragment
): {
  id: string | number
  type: string
  label: string
  options: PWAdminCmdKOption[]
} {
  const typename = model.__typename!

  function getUILink(url: string): PWAdminCmdKOption {
    return {
      label: "View in UI",
      icon: "external-link",
      href: url
    }
  }

  function getAdminLink(url: string): PWAdminCmdKOption {
    return {
      label: "View in Admin",
      icon: "external-link",
      href: url
    }
  }

  switch (model.__typename) {
    case "School":
      return {
        type: "School",
        id: model.id,
        label: model.name,
        options: [
          getAdminLink(
            getAdminUrl(`admin/school/school/${model.id}/`).toString()
          ),
          getUILink(model.url!)
        ]
      }
    case "Resource":
      return {
        type: "Resource",
        id: model.id,
        label: model.name,
        options: [
          getAdminLink(
            getAdminUrl(`admin/course/course/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}library/${model.slug}/`)
        ]
      }
    case "User":
      const options: PWAdminCmdKOption[] = [
        getAdminLink(getAdminUrl(`admin/auth/user/${model.id}/`).toString()),
        getUILink(model.email!)
      ]

      if (model.email) {
        options.push({
          type: "other" as PWAdminCmdKOption["type"],
          label: "Authenticate as User",
          icon: "avatar",
          href: getPWAdminSSOUrl(`admin::${model.email}`)
        })
      }

      return {
        type: "User",
        id: model.id,
        label: model.full_name,
        options
      }
    case "Banner":
      return {
        type: "Banner",
        id: model.id!,
        label: model.heading || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/library/banner/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}manage/library/banner/${model.id!}/`)
        ]
      }
    case "Author":
      return {
        type: "Author",
        id: model.id!,
        label: model.full_name || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/course/courseauthor/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}manage/library/author/${model.id!}/`)
        ]
      }
    case "Category":
      return {
        type: "Category",
        id: model.id!,
        label: model.name || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/course/coursecategory/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}manage/library/category/${model.id!}/`)
        ]
      }
    case "PathItem":
      return {
        type: `Path Item (${
          model.type === PathItemType.Divider
            ? "Divider"
            : model.type === PathItemType.Lesson
            ? "Lesson"
            : "Step"
        })`,
        id: model.id!,
        label: model.name || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/course/scheduleditem/${model.id}/`).toString()
          ),
          getUILink(
            `${school.url!}/library/${model.context?.resource_id}/${
              model.context?.cohort_id
            }/path/${
              model.type === PathItemType.Lesson ? "lesson" : "step"
            }/${model.id!}/`
          ),
          // View all completions in Inbox for a given source item (so across all Cohorts), whether archived or not.
          {
            label: "View all completions in Inbox",
            icon: "envelope",
            to: `/inbox/?source_id=${model.source_id}&filter=all&archived_date_filter={"eq":null},{"op":"or"},{"neq":null}`
          }
        ]
      }
    case "StripeProduct":
      return {
        type: "StripeProduct",
        id: model.id!,
        label: model.name || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/payment/stripeproduct/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}subscribe/`),
          {
            label: "View in Stripe Dashboard",
            icon: "external-link",
            href: model.stripe_connected_account_url || ""
          }
        ]
      }
    case "StripePrice":
      return {
        type: "StripePrice",
        id: model.id!,
        label: model.nickname || "",
        options: [
          getAdminLink(
            getAdminUrl(`admin/payment/stripeprice/${model.id}/`).toString()
          ),
          getUILink(`${school.url!}subscribe/`),
          {
            label: "View in Stripe Dashboard",
            icon: "external-link",
            href: model.stripe_connected_account_url || ""
          }
        ]
      }
    case "SubscriptionPlan":
      return {
        type: "SchoolSubscriptionPlan",
        id: model.id!,
        label: model.name || "",
        options: [
          getAdminLink(
            getAdminUrl(
              `admin/payment/subscriptionplan/${model.id}/`
            ).toString()
          ),
          getUILink(`${school.url!}subscribe/`),
          {
            label: "View in Stripe Dashboard",
            icon: "external-link",
            href: model.stripe_connected_account_url || ""
          }
        ]
      }
    default:
      // Struggling to get the exhaustiveGuard working without losing typing of model
      // within the switch cases.
      return {
        id: -1,
        type: "Unknown",
        label: "Unknown",
        options: []
      }
  }
}

function PWAdminHintContent({ model }: { model: PWAdminHintModel }) {
  const { school } = usePathwrightContext()
  const { setHintsActive } = usePWAdminCmdKContext()
  const data = getModelInfo(model, school)
  const supportInfoOption = useSupportInfo(model)
  const options = [...data.options, supportInfoOption]

  // Disable hints when pressing Escape with hint open.
  useKeyControl("Escape", () => setHintsActive(false))

  return (
    <PWAdminCmdKMenu>
      <PWAdminCmdKItems options={options} />
    </PWAdminCmdKMenu>
  )
}

function PWAdminHint({ children, model, wrap, portal }: PWAdminHintProps) {
  const { hintsActive } = usePWAdminCmdKContext()

  const MaybePortal = portal ? Portal : Fragment
  const portalProps = portal === true ? {} : portal

  return useIsPWAdminUtilEnabled() && hintsActive ? (
    <Popover trigger="hover" isLazy closeOnEsc returnFocusOnClose>
      <>
        <PopoverTrigger>
          {wrap ? <div>{children}</div> : children}
        </PopoverTrigger>
        <MaybePortal {...portalProps}>
          <PopoverContent
            border="none"
            borderRadius="card"
            // May want to remove boxShadow, but it helps with contrast against
            // dark bg.
            boxShadow="0 0 1px 2px var(--chakra-colors-whiteAlpha-600)"
            p={0}
          >
            <PopoverArrow
              border="none"
              backgroundColor="black"
              shadowColor="black"
            />
            <PopoverBody p={0}>
              <PWAdminHintContent model={model} />
            </PopoverBody>
          </PopoverContent>
        </MaybePortal>
      </>
    </Popover>
  ) : (
    children
  )
}

export default PWAdminHint
